import React, { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { resolveNavForRole, ADMIN_NAV_CONFIG } from '../config/navigation';
import './Breadcrumbs.css';

// Look up section + item label for the current pathname so users know
// where they are without relying on the page-header title alone.
function useBreadcrumbSegments() {
  const location = useLocation();
  const { user } = useAuth();
  const activeRole = user?.activeContext?.role || user?.user?.role || null;

  return useMemo(() => {
    const path = location.pathname;
    const cfgs = [
      { root: '/practice-view', sections: resolveNavForRole(activeRole) || [] },
      { root: '/admin', sections: activeRole === 'super_admin' ? ADMIN_NAV_CONFIG : [] },
    ];

    for (const { root, sections } of cfgs) {
      if (!path.startsWith(root)) continue;
      let best = null;
      for (const section of sections) {
        for (const item of section.items) {
          if (path === item.path || path.startsWith(item.path + '/')) {
            if (!best || item.path.length > best.item.path.length) {
              best = { section, item };
            }
          }
        }
      }
      if (best) {
        return [
          { label: root === '/admin' ? 'Admin' : 'Practice', to: root },
          { label: best.section.label },
          { label: best.item.label, to: best.item.path },
        ];
      }
      return [{ label: root === '/admin' ? 'Admin' : 'Practice', to: root }];
    }
    return [];
  }, [location.pathname, activeRole]);
}

function Breadcrumbs() {
  const segments = useBreadcrumbSegments();
  if (segments.length === 0) return null;

  return (
    <nav className="breadcrumbs" aria-label="Breadcrumb">
      {segments.map((seg, idx) => {
        const isLast = idx === segments.length - 1;
        return (
          <React.Fragment key={idx}>
            {seg.to && !isLast ? (
              <Link to={seg.to} className="breadcrumbs__link">{seg.label}</Link>
            ) : (
              <span className={`breadcrumbs__crumb ${isLast ? 'breadcrumbs__crumb--current' : ''}`}>
                {seg.label}
              </span>
            )}
            {!isLast && <span className="breadcrumbs__sep" aria-hidden="true">›</span>}
          </React.Fragment>
        );
      })}
    </nav>
  );
}

export default Breadcrumbs;
